import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import RefreshIcon from '@mui/icons-material/Refresh'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface AgentStatus {
  agent: {
    id: number
    username: string
    slug: string
    agent_type: string
    memory: string | null
  }
  status: 'online' | 'idle' | 'error' | 'offline'
  last_heartbeat_at: string | null
  current_task: { id: number; summary: string; board_slug: string } | null
  heartbeat_metadata: Record<string, unknown>
  latest_session_log: {
    id: number
    session_id: string
    status: string
    started_at: string
  } | null
  error_count_24h: number
  recent_error_types: Record<string, number>
}

interface Heartbeat {
  id: number
  status: string
  reported_at: string
  metadata: Record<string, unknown>
  current_task: { id: number; summary: string } | null
}

interface AgentError {
  id: number
  error_type: string
  message: string
  occurred_at: string
  session_log_id: number | null
  task_id: number | null
}

interface Props {
  agent_status: AgentStatus
  heartbeats: Heartbeat[]
  recent_errors: AgentError[]
}

const STATUS_CONFIG: Record<string, { color: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  online: { color: 'success', label: 'Online' },
  idle: { color: 'warning', label: 'Idle' },
  error: { color: 'error', label: 'Error' },
  offline: { color: 'default', label: 'Offline' },
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)

  if (diffSecs < 60) return `${diffSecs}s ago`
  const diffMins = Math.floor(diffSecs / 60)
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

const REFRESH_INTERVAL = 30_000

export default function Show({ agent_status, heartbeats, recent_errors }: Props) {
  const as = agent_status
  const config = STATUS_CONFIG[as.status] || STATUS_CONFIG.offline
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        router.reload({ preserveState: true })
      }, REFRESH_INTERVAL)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh])

  function handleManualRefresh() {
    router.reload({ preserveState: true })
  }

  const errorTypeSummary = Object.entries(as.recent_error_types).sort(
    ([, a], [, b]) => b - a
  )

  const metadataEntries = Object.entries(as.heartbeat_metadata).filter(
    ([, v]) => v !== null && v !== undefined
  )

  return (
    <SidebarLayout section="monitoring" title={`@${as.agent.username}`}>
      {/* Back link + refresh controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          component={Link}
          href="/monitoring/agent_statuses"
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          All Agents
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={handleManualRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh (30s)'}>
            <IconButton size="small" onClick={() => setAutoRefresh(!autoRefresh)}>
              {autoRefresh ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {autoRefresh && (
            <Typography variant="caption" color="text.secondary">
              Auto-refresh: 30s
            </Typography>
          )}
        </Box>
      </Box>

      {/* Status Summary Card */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                icon={
                  as.status === 'online' ? (
                    <FiberManualRecordIcon
                      sx={{
                        fontSize: 10,
                        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }}
                    />
                  ) : undefined
                }
                label={config.label}
                color={config.color}
                variant="outlined"
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Type</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {as.agent.agent_type === 'machine' ? 'AI Agent' : 'Human'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last Heartbeat</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {as.last_heartbeat_at ? formatTimeAgo(as.last_heartbeat_at) : 'Never'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Errors (24h)</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {as.error_count_24h}
            </Typography>
          </Box>
          {as.current_task && (
            <Box>
              <Typography variant="caption" color="text.secondary">Current Task</Typography>
              <Typography
                variant="body2"
                component={Link}
                href={`/tasks/boards/${as.current_task.board_slug}/tasks/${as.current_task.id}`}
                sx={{ mt: 0.5, display: 'block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {as.current_task.summary}
              </Typography>
            </Box>
          )}
          {as.latest_session_log && (
            <Box>
              <Typography variant="caption" color="text.secondary">Latest Session</Typography>
              <Typography
                variant="body2"
                component={Link}
                href={`/monitoring/session_logs/${as.latest_session_log.id}`}
                sx={{ mt: 0.5, display: 'block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {as.latest_session_log.status === 'streaming' ? 'Live' : as.latest_session_log.status}
                {' - '}
                {formatTimeAgo(as.latest_session_log.started_at)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Metadata */}
        {metadataEntries.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Heartbeat Metadata
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {metadataEntries.map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${String(value)}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Memory */}
      {as.agent.agent_type === 'machine' && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6">Memory</Typography>
            <Button
              component={Link}
              href={`/manage/agents/${as.agent.slug}/edit`}
              size="small"
            >
              Edit
            </Button>
          </Box>
          {as.agent.memory ? (
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                bgcolor: 'action.hover',
                p: 2,
                borderRadius: 1,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {as.agent.memory}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No memory stored.
            </Typography>
          )}
        </Paper>
      )}

      {/* Error Type Summary */}
      {errorTypeSummary.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>Error Summary (24h)</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Error Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorTypeSummary.map(([type, count]) => (
                  <TableRow key={type}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                        {type}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={count}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.75rem', minWidth: 32 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Heartbeat History */}
      <Typography variant="h6" sx={{ mb: 2 }}>Heartbeat History</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Task</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {heartbeats.map((hb) => (
              <TableRow key={hb.id}>
                <TableCell>
                  <Tooltip title={formatDateTime(hb.reported_at)}>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimeAgo(hb.reported_at)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={hb.status}
                    size="small"
                    color={
                      hb.status === 'online' ? 'success' :
                      hb.status === 'idle' ? 'warning' :
                      hb.status === 'error' ? 'error' : 'default'
                    }
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.75rem' }}
                  />
                </TableCell>
                <TableCell>
                  {hb.current_task ? (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {hb.current_task.summary}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {heartbeats.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No heartbeats recorded.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Recent Errors */}
      <Typography variant="h6" sx={{ mb: 2 }}>Recent Errors</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Session</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recent_errors.map((err) => (
              <TableRow key={err.id}>
                <TableCell>
                  <Tooltip title={formatDateTime(err.occurred_at)}>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimeAgo(err.occurred_at)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                    {err.error_type}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, wordBreak: 'break-word' }}>
                    {err.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  {err.session_log_id ? (
                    <Typography
                      variant="body2"
                      component={Link}
                      href={`/monitoring/session_logs/${err.session_log_id}`}
                      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      View
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {recent_errors.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No recent errors.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SidebarLayout>
  )
}
